import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { CalendarIcon, Eye, FileText, MoreHorizontal, Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import { toast } from 'sonner';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';
import { CreateOrModifyAppointmentDrawer } from '~/pages/AppointmentsCalendar';
import { useCreateOrModifyAppointmentStore } from '~/stores/create-appointment-store';
import { useAppointmentsCalendarStore } from '~/stores/appointments-calendar-store';

type BillingDraftRow = {
    id: string;
    billingDraftNumber: string;
    billingDraftDate: string;
    status: 'PENDING_PAYMENT' | 'PAID' | 'CANCELLED';
    type: 'NORMAL' | 'INFLUENCER' | 'MARKETING' | 'ACADEMY' | 'KULANZ' | 'CANCELLATION_FEE';
    totalGross: number;
    totalGrossDiscounted: number;
    totalPaidCents: number;
    customer: {
        id: number;
        firstName: string;
        lastName: string;
        email: string | null;
    };
    store: {
        id: number;
        name: string;
    };
    appointment: {
        id: string;
        title: string;
        steps: {
            dateFrom: string;
            dateUntil: string;
        }[];
    } | null;
};

const statusLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    PENDING_PAYMENT: { label: 'Ausstehend', variant: 'outline' },
    PAID: { label: 'Bezahlt', variant: 'default' },
    CANCELLED: { label: 'Storniert', variant: 'destructive' },
};

const billingDraftTypeLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    NORMAL: { label: 'Standard', variant: 'outline' },
    INFLUENCER: { label: 'Influencer', variant: 'secondary' },
    MARKETING: { label: 'Marketing', variant: 'secondary' },
    ACADEMY: { label: 'Academy', variant: 'secondary' },
    KULANZ: { label: 'Kulanz/Beschwerde', variant: 'secondary' },
    CANCELLATION_FEE: { label: 'Ausfallentschädigung', variant: 'secondary' },
};

export type BillingDraftsTableFixedFilters = {
    hasCancellationFee?: boolean;
};

type BillingDraftsTableProps = {
    fixedFilters?: BillingDraftsTableFixedFilters;
};

export const BillingDraftsTable = ({ fixedFilters }: BillingDraftsTableProps) => {
    const navigate = useNavigate();
    const { role } = useUserStore(
        useShallow((state) => ({ role: state.user?.role }))
    );
    const isAdmin = role === UserRole.ADMIN;

    const handleOpenAppointmentDrawer = (appointmentId: string, store: { id: number; name: string }) => {
        useCreateOrModifyAppointmentStore.setState({
            appointmentId,
            mode: 'modify',
            notifyCustomer: false,
        });
        useAppointmentsCalendarStore.setState({
            selectedStore: { id: store.id, label: store.name },
            createOrModifyAppointmentDrawerOpen: true,
        });
    };

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStoreId, setSelectedStoreId] = useState<string>(isAdmin ? 'all' : '');
    const [selectedStatus, setSelectedStatus] = useState<string>('all');
    const [selectedType, setSelectedType] = useState<string>('all');
    const [page, setPage] = useState(0);
    const pageSize = 20;

    const { data: storesData } = trpc.stores.getAll.useQuery({ filters: {} });
    const stores = useMemo(() => {
        const data = storesData?.data || [];
        return [...data].sort((a, b) => a.name.localeCompare(b.name));
    }, [storesData?.data]);

    useEffect(() => {
        if (isAdmin && selectedStoreId === '') {
            setSelectedStoreId('all');
        } else if (!isAdmin && stores.length > 0 && !selectedStoreId) {
            setSelectedStoreId(String(stores[0].id));
        }
    }, [stores, isAdmin, selectedStoreId]);

    // Fetch billing drafts
    const { data: billingDraftsData, isLoading, refetch } = trpc.billingDrafts.getAll.useQuery({
        page,
        pageSize,
        filters: {
            searchTerm: searchTerm || undefined,
            storeId: selectedStoreId !== 'all' ? parseInt(selectedStoreId) : undefined,
            status: selectedStatus !== 'all' ? selectedStatus as 'PENDING_PAYMENT' | 'PAID' | 'CANCELLED' : undefined,
            type: selectedType !== 'all' ? selectedType as 'NORMAL' | 'INFLUENCER' | 'MARKETING' | 'ACADEMY' | 'KULANZ' | 'CANCELLATION_FEE' : undefined,
            ...fixedFilters,
        },
    }, {
        enabled: selectedStoreId !== '',
    });

    const columns: ColumnDef<BillingDraftRow>[] = [
        {
            accessorKey: 'billingDraftNumber',
            header: 'Entwurfsnr.',
            cell: ({ row }) => (
                <div className='font-mono text-sm'>{row.original.billingDraftNumber}</div>
            ),
        },
        {
            id: 'customer',
            header: 'Kunde',
            cell: ({ row }) => {
                const customer = row.original.customer;
                return (
                    <div>
                        <div
                            className='font-medium cursor-pointer hover:underline text-primary'
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customer/${customer.id}`);
                            }}
                        >
                            {customer.firstName} {customer.lastName}
                        </div>
                        {customer.email && (
                            <div className='text-sm text-muted-foreground'>{customer.email}</div>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'store',
            header: 'Filiale',
            cell: ({ row }) => row.original.store.name,
        },
        {
            accessorKey: 'billingDraftDate',
            header: 'Datum',
            cell: ({ row }) => {
                const date = new Date(row.original.billingDraftDate);
                return format(date, 'dd.MM.yyyy', { locale: de });
            },
        },
        {
            id: 'appointment',
            header: 'Termin',
            cell: ({ row }) => {
                const appointment = row.original.appointment;
                if (!appointment || appointment.steps.length === 0) {
                    return <span className='text-muted-foreground'>–</span>;
                }
                const dateFrom = new Date(appointment.steps[0].dateFrom);
                return (
                    <div
                        className='flex items-center gap-1.5 cursor-pointer hover:underline text-primary'
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenAppointmentDrawer(appointment.id, row.original.store);
                        }}
                    >
                        <CalendarIcon className='h-3.5 w-3.5 flex-shrink-0' />
                        <div>
                            <div className='text-sm'>{format(dateFrom, 'dd.MM.yyyy', { locale: de })}</div>
                            <div className='text-sm text-muted-foreground'>{format(dateFrom, 'HH:mm', { locale: de })} Uhr</div>
                        </div>
                    </div>
                );
            },
        },
        {
            accessorKey: 'type',
            header: 'Typ',
            cell: ({ row }) => {
                const typeInfo = billingDraftTypeLabels[row.original.type] || { label: row.original.type, variant: 'outline' as const };
                return (
                    <Badge variant={typeInfo.variant}>
                        {typeInfo.label}
                    </Badge>
                );
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }) => {
                const status = statusLabels[row.original.status];
                return (
                    <Badge variant={status.variant}>
                        {status.label}
                    </Badge>
                );
            },
        },
        {
            accessorKey: 'totalGrossDiscounted',
            header: 'Betrag',
            cell: ({ row }) => {
                const amount = Number(row.original.totalGrossDiscounted);
                const isNegative = amount < 0;
                return (
                    <div className={`font-medium ${isNegative ? 'text-red-600' : ''}`}>
                        {new Intl.NumberFormat('de-DE', {
                            style: 'currency',
                            currency: 'EUR',
                        }).format(amount)}
                    </div>
                );
            },
        },
        {
            id: 'totalPaid',
            header: 'Zahlung',
            cell: ({ row }) => {
                if (row.original.type === 'INFLUENCER' || row.original.type === 'MARKETING' || row.original.type === 'KULANZ') {
                    return <div className='font-medium text-muted-foreground'>-</div>;
                }

                const totalPaidEur = row.original.totalPaidCents / 100;
                const totalGrossDiscounted = Number(row.original.totalGrossDiscounted);
                const isMatch = Math.abs(totalPaidEur - totalGrossDiscounted) < 0.01;

                return (
                    <div className={`font-medium ${isMatch ? 'text-green-600' : 'text-red-600'}`}>
                        {new Intl.NumberFormat('de-DE', {
                            style: 'currency',
                            currency: 'EUR',
                        }).format(totalPaidEur)}
                    </div>
                );
            },
        },
        {
            id: 'actions',
            cell: ({ row }) => {
                const billingDraft = row.original;

                return (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant='ghost' className='h-8 w-8 p-0'>
                                <span className='sr-only'>Menü öffnen</span>
                                <MoreHorizontal className='h-4 w-4' />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                            <DropdownMenuItem onClick={() => navigate(`/billing-draft/${billingDraft.id}`)}>
                                <Eye className='mr-2 h-4 w-4' />
                                Anzeigen
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(billingDraft.billingDraftNumber)
                                toast.success('Entwurfsnummer kopiert');
                            }}>
                                <FileText className='mr-2 h-4 w-4' />
                                Entwurfsnr. kopieren
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                );
            },
        },
    ];

    const table = useReactTable({
        data: billingDraftsData?.data as BillingDraftRow[] || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        pageCount: billingDraftsData?.pageCount || 0,
    });

    return (
        <>
            <CreateOrModifyAppointmentDrawer onSubmit={() => refetch()} />
            {/* Filters */}
            <Card className='mb-6'>
                <CardContent className='pt-6'>
                    <div className='flex flex-wrap gap-4'>
                        <div className='flex-1 min-w-[200px]'>
                            <div className='relative'>
                                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                                <Input
                                    placeholder='Suche nach Kunde oder Entwurfsnummer...'
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setPage(0);
                                    }}
                                    className='pl-10'
                                />
                            </div>
                        </div>
                        <Select
                            value={selectedStoreId}
                            onValueChange={(value) => {
                                setSelectedStoreId(value);
                                setPage(0);
                            }}
                        >
                            <SelectTrigger className='w-[200px]'>
                                <SelectValue placeholder='Alle Filialen' />
                            </SelectTrigger>
                            <SelectContent>
                                {isAdmin && <SelectItem value='all'>Alle Filialen</SelectItem>}
                                {stores.map((store) => (
                                    <SelectItem key={store.id} value={String(store.id)}>
                                        {store.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select
                            value={selectedStatus}
                            onValueChange={(value) => {
                                setSelectedStatus(value);
                                setPage(0);
                            }}
                        >
                            <SelectTrigger className='w-[200px]'>
                                <SelectValue placeholder='Alle Status' />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value='all'>Alle Status</SelectItem>
                                <SelectItem value='PENDING_PAYMENT'>Ausstehend</SelectItem>
                                <SelectItem value='PAID'>Bezahlt</SelectItem>
                                <SelectItem value='CANCELLED'>Storniert</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select
                            value={selectedType}
                            onValueChange={(value) => {
                                setSelectedType(value);
                                setPage(0);
                            }}
                        >
                            <SelectTrigger className='w-[200px]'>
                                <SelectValue placeholder='Alle Typen' />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value='all'>Alle Typen</SelectItem>
                                <SelectItem value='NORMAL'>Standard</SelectItem>
                                <SelectItem value='INFLUENCER'>Influencer</SelectItem>
                                <SelectItem value='MARKETING'>Marketing</SelectItem>
                                <SelectItem value='ACADEMY'>Academy</SelectItem>
                                <SelectItem value='KULANZ'>Kulanz/Beschwerde</SelectItem>
                                <SelectItem value='CANCELLATION_FEE'>Ausfallentschädigung</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card>
                <CardContent className='p-0'>
                    {isLoading ? (
                        <div className='p-6 space-y-4'>
                            {[...Array(5)].map((_, i) => (
                                <Skeleton key={i} className='h-12 w-full' />
                            ))}
                        </div>
                    ) : (
                        <>
                            <Table>
                                <TableHeader>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <TableRow key={headerGroup.id}>
                                            {headerGroup.headers.map((header) => (
                                                <TableHead key={header.id}>
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                </TableHead>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHeader>
                                <TableBody>
                                    {table.getRowModel().rows?.length ? (
                                        table.getRowModel().rows.map((row) => (
                                            <TableRow
                                                key={row.id}
                                                className='cursor-pointer hover:bg-muted/50 relative'
                                            >
                                                {row.getVisibleCells().map((cell, index) => (
                                                    <TableCell key={cell.id}>
                                                        {index === 0 && (
                                                            <Link
                                                                to={`/billing-draft/${row.original.id}`}
                                                                className="absolute inset-0 z-[1]"
                                                                tabIndex={-1}
                                                            />
                                                        )}
                                                        {cell.column.id === 'actions' || cell.column.id === 'customer' || cell.column.id === 'appointment' ? (
                                                            <div className="relative z-[2]">
                                                                {flexRender(
                                                                    cell.column.columnDef.cell,
                                                                    cell.getContext()
                                                                )}
                                                            </div>
                                                        ) : (
                                                            flexRender(
                                                                cell.column.columnDef.cell,
                                                                cell.getContext()
                                                            )
                                                        )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={columns.length}
                                                className='h-24 text-center'
                                            >
                                                Keine Aufträge gefunden.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>

                            {/* Pagination */}
                            {billingDraftsData && billingDraftsData.pageCount > 1 && (
                                <div className='flex items-center justify-between px-4 py-4 border-t'>
                                    <div className='text-sm text-muted-foreground'>
                                        Seite {page + 1} von {billingDraftsData.pageCount} ({billingDraftsData.totalCount} Aufträge)
                                    </div>
                                    <div className='flex gap-2'>
                                        <Button
                                            variant='outline'
                                            size='sm'
                                            onClick={() => setPage(page - 1)}
                                            disabled={page === 0}
                                        >
                                            Zurück
                                        </Button>
                                        <Button
                                            variant='outline'
                                            size='sm'
                                            onClick={() => setPage(page + 1)}
                                            disabled={page >= billingDraftsData.pageCount - 1}
                                        >
                                            Weiter
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </>
    );
};
